/**
 * useArTableFiltering
 *
 * Hook for computing filtered row IDs.
 * Visibility-only filtering - does not affect persisted data.
 *
 * Performance optimization: Only accesses rowsDocs when filtering is enabled,
 * preventing ArTableBody from rerendering on every cell edit when not filtering.
 */

import { useMemo } from 'react';
import type { ArRowData, ArRowId, TypeId } from '#types/System.Artifacts.types';
import type { DocEntry } from '../../../store/Store.types';
import {
  filterArRowIdsByColumnFilters,
  getCellDisplayValue,
} from './filterArRowIdsByColumnFilters';

type ReadonlyFilterState = {
  readonly byColumnId: Readonly<Record<TypeId, string>>;
};

type UseArTableFilteringParams = {
  visibleRowIds: ArRowId[];
  filterState: ReadonlyFilterState;
  rowsDocs: Map<string, DocEntry<ArRowData>> | null;
};

type UseArTableFilteringResult = {
  filteredRowIds: ArRowId[];
  hasActiveFilters: boolean;
  filteredOutCount: number;
};

function useArTableFiltering({
  visibleRowIds,
  filterState,
  rowsDocs,
}: UseArTableFilteringParams): UseArTableFilteringResult {
  const activeFilters = filterState.byColumnId;

  const hasActiveFilters = useMemo(() => {
    return Object.values(activeFilters).some((v) => v && v.length > 0);
  }, [activeFilters]);

  return useMemo(() => {
    if (!hasActiveFilters || !rowsDocs) {
      return {
        filteredRowIds: visibleRowIds,
        hasActiveFilters,
        filteredOutCount: 0,
      };
    }

    const activeFilterEntries = Object.entries(activeFilters).filter(
      (_entry): _entry is [TypeId, string] => {
        const value = _entry[1];
        return typeof value === 'string' && value.length > 0;
      }
    );

    const filteredRowIds = filterArRowIdsByColumnFilters({
      rowIds: visibleRowIds,
      rowsDocs,
      filterEntries: activeFilterEntries,
    });

    return {
      filteredRowIds,
      hasActiveFilters,
      filteredOutCount: visibleRowIds.length - filteredRowIds.length,
    };
  }, [activeFilters, hasActiveFilters, rowsDocs, visibleRowIds]);
}

export { useArTableFiltering, getCellDisplayValue };
export type { UseArTableFilteringParams, UseArTableFilteringResult };
